/* eslint-disable @typescript-eslint/naming-convention */
import {
  IFdApplicationType,
  IFdOperatingCost,
  IFdProductionCost,
  IFdProductionPlan,
} from 'constants/interfaces';

import {lshParseOperatingCostMonthlyCosts} from './operatingCostMonthlyCosts';

/* ─── Cash-flow grid helpers ─── */
export const MONTHS = [
  'jan',
  'feb',
  'mar',
  'apr',
  'may',
  'jun',
  'jul',
  'aug',
  'sep',
  'oct',
  'nov',
  'dec',
] as const;
export type MonthKey = (typeof MONTHS)[number];

export type MonthlyCostsCfg = {
  total: string;
  months: string[];
  quarters: string[];
  frequency: 'monthly' | 'once-off' | 'quarterly';
};

/* ─── Basic maths helpers used everywhere ─── */
export function getProductionPlanRevenue(plans: IFdProductionPlan[]): number {
  return plans.reduce(
    (s, p) =>
      s +
      (p.numberOfHectares || 0) *
        (p.estimatedYield || 0) *
        (p.estimatedPrice || 0),
    0,
  );
}

export function getTotalProductionCosts(costs: IFdProductionCost[]): number {
  return costs.reduce((s, c) => s + c.seed + c.fertiliser + c.chemical, 0);
}

export function getTotalOperatingCosts(costs: IFdOperatingCost[]): number {
  return costs.reduce((sum, cost) => {
    const {total, monthlyValues} = lshParseOperatingCostMonthlyCosts(
      cost.monthlyCosts,
    );
    if (Object.keys(monthlyValues).length > 0) {
      return sum + total;
    }

    try {
      const cfg: MonthlyCostsCfg = JSON.parse(cost.monthlyCosts);
      switch (cfg.frequency) {
        case 'monthly':
          return sum + parseFloat(cfg.total) * (cfg.months?.length ?? 0);
        case 'quarterly':
          return sum + parseFloat(cfg.total) * (cfg.quarters?.length ?? 0);
        case 'once-off':
          return sum + parseFloat(cfg.total);
        default:
          return sum;
      }
    } catch {
      return sum;
    }
  }, 0);
}

/* ─── Historic + 2025 projection (mini P&L) ─── */
export function buildFinancialProjection(app?: IFdApplicationType) {
  const base =
    app?.financialPerformance?.map((fp) => ({
      year: fp.year,
      revenue: fp.revenue,
      costOfSales: fp.expenses,
      operatingCosts: fp.operatingCosts || 0,
      grossProfit: fp.revenue - fp.expenses,
      netProfit: fp.revenue - fp.expenses - (fp.operatingCosts || 0),
    })) ?? [];

  base.sort((a, b) => a.year - b.year);

  const rev25 = getProductionPlanRevenue(app?.productionPlans ?? []);
  const cos25 = getTotalProductionCosts(
    app?.productionCosts?.filter((c) => c.year === 2025) ?? [],
  );
  const op25 = getTotalOperatingCosts(
    app?.operatingCosts?.filter((c) => c.year === 2025) ?? [],
  );

  base.push({
    year: 2025,
    revenue: rev25,
    costOfSales: cos25,
    operatingCosts: op25,
    grossProfit: rev25 - cos25,
    netProfit: rev25 - cos25 - op25,
  });

  return base;
}

export function fmtCurrency(
  value: number | string,
  opts?: {decimals?: number},
): string {
  let n = Number(value);
  if (!Number.isFinite(n)) n = 0;

  const decimals =
    typeof opts?.decimals === 'number'
      ? opts.decimals
      : Math.abs(n) % 1 !== 0
      ? 2
      : 0;

  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(n);
}
